<template>
  <div class="physical-part-list">
    <Teleport to="body">
      <img
        v-if="zoomSrc"
        :src="zoomSrc"
        class="part-image-zoom"
        :style="{ left: zoomPos.x + 'px', top: zoomPos.y + 'px' }"
      >
    </Teleport>

    <DataTable
      :value="parts"
      :loading="loading"
      selection-mode="single"
      @row-click="select"
      data-testid="physical-part-datatable"
      show-gridlines
    >
      <template #empty>
        <slot name="empty" />
      </template>

      <!-- Image column -->
      <Column field="image">
        <template #body="slotProps">
          <div class="part-image-wrapper">
            <img
              v-if="slotProps.data.image"
              :src="slotProps.data.image"
              class="part-image"
              @mouseenter="showZoom($event, slotProps.data.image)"
              @mouseleave="hideZoom"
            >
          </div>
        </template>
      </Column>

      <!-- Manufacturer column -->
      <Column :header="$t('PhysicalPartList.manufacturer')" field="manufacturer" />

      <!-- MPN column -->
      <Column :header="$t('PhysicalPartList.mpn')" field="mpn">
        <template #body="slotProps">
          <div>
            <span v-html="slotProps.data.mpnFormatted || slotProps.data.mpn" />
            <a
              type="button"
              class="text-secondary icon-action"
              v-if="slotProps.data.mpn"
              v-bs-tooltip="{ title: $t('PhysicalPartList.copyMpnTooltip'), placement: 'top' }"
              @click.stop="copyMpnToClipboard($event, slotProps.data.mpn)"
            >
              <i class="fa-regular fa-copy" />
            </a>
            <div class="text-secondary" v-if="slotProps.data.aliasFormatted">
              {{ $t('PhysicalPartList.alsoKnownAs') }}: <span v-html="slotProps.data.aliasFormatted" />
            </div>
          </div>
        </template>
      </Column>

      <!-- Description column -->
      <Column :header="$t('PhysicalPartList.description')" field="description" style="width: 35%">
        <template #body="slotProps">
          <div>
            {{ slotProps.data.description }}
            <div class="text-secondary" v-if="slotProps.data.longDescription">
              {{ slotProps.data.longDescription }}
            </div>
          </div>
        </template>
      </Column>

      <!-- Package column -->
      <Column :header="$t('PhysicalPartList.footprint')" field="package">
        <template #body="slotProps">
          <span @click.stop>
            <span v-if="slotProps.data.package">{{ slotProps.data.package }}</span>
            <em v-else class="text-secondary">
              <span v-if="slotProps.data.datasheet">{{ $t('PhysicalPartList.checkDataSheet') }}</span>
              <span v-else>{{ $t('PhysicalPartList.footprintUnknown') }}</span>
            </em>
          </span>
        </template>
      </Column>

      <!-- Datasheet column -->
      <Column
        :header="$t('PhysicalPartList.datasheetColumn')"
        field="datasheet"
        body-class="text-center"
      >
        <template #body="slotProps">
          <a
            v-if="slotProps.data.datasheet"
            :href="slotProps.data.datasheet"
            target="_blank"
            class="text-secondary"
            @click.stop
          >
            <i class="fa-regular fa-file-pdf fa-lg" />
          </a>
        </template>
      </Column>

      <!-- Availability/Price column -->
      <Column field="price">
        <template #header>
          <div class="p-datatable-column-header-content">
            <span class="p-datatable-column-title">{{ $t('PhysicalPartList.availability') }}</span>
            <i
              class="fa fa-question-circle text-secondary ms-1"
              v-bs-tooltip="{ title: $t('PhysicalPartList.availabilityTooltip'), placement: 'bottom' }"
            />
          </div>
        </template>
        <template #body="slotProps">
          <span class="d-flex align-items-center">
            <span class="me-1">
              <StockIndicator
                v-if="slotProps.data.availability"
                :availability="slotProps.data.availability"
                class="status"
              />
            </span>
            <span>
              {{ formatPrice(slotProps.data.price) }}
            </span>
          </span>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { Tooltip } from 'bootstrap';

import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import messages from 'locales/PhysicalPartList';
import StockIndicator from '../StockIndicator.vue';

export default {
  name: 'PhysicalPartList',
  components: {
    DataTable,
    Column,
    StockIndicator,
  },
  props: {
    parts: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['selectPart'],
  data() {
    return {
      zoomSrc: null,
      zoomPos: { x: 0, y: 0 },
    };
  },
  methods: {
    showZoom(event, src) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.zoomSrc = src;
      this.zoomPos = { x: rect.right + 8, y: rect.top + rect.height / 2 };
    },
    hideZoom() {
      this.zoomSrc = null;
    },
    select(event) {
      this.$emit('selectPart', event.data);
    },
    formatPrice(price) {
      return price ?? this.$t('PhysicalPartList.unknownPrice');
    },
    async copyMpnToClipboard(event, mpn) {
      const el = event.currentTarget;
      await navigator.clipboard.writeText(mpn);
      const tt = Tooltip.getInstance(el);
      tt.setContent({ '.tooltip-inner': this.$t('PhysicalPartList.copied') });
      setTimeout(() => {
        tt.setContent({ '.tooltip-inner': this.$t('PhysicalPartList.copyMpnTooltip') });
      }, 1000);
    },
  },
  i18n: { messages },
};
</script>

<style scoped lang="scss">
@use 'stylesheets/bootstrap-customers/colors';

.physical-part-list {
  .p-datatable-thead > tr > th {
    color: colors.$black;
  }

  .p-datatable-tbody > tr > td {
    color: colors.$black;
    vertical-align: middle;
    padding: 0.5rem 0.625rem 0.625rem;
  }

  .p-datatable-scrollable-body {
    overflow: auto;
  }

  .p-datatable-loading {
    position: absolute;
    background: rgba(255, 255, 255, 0.6);
    z-index: 3;
    width: 100%;
    height: 100%;
  }
}

.icon-action {
  margin-left: .25rem;
}

.part-image-wrapper {
  display: inline-block;

  .part-image {
    width: 3rem;
    height: 3rem;
    object-fit: contain;
    vertical-align: middle;
  }
}
.part-image-zoom {
  position: fixed;
  transform: translateY(-50%);
  width: 8rem;
  height: 8rem;
  object-fit: contain;
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  pointer-events: none;
}
</style>
